import { Color } from 'three';
import clothing from '../../data/visitorClothing.json';
import { citizenGeometry } from '../world/life/citizens/citizenShapes';
import type { CitizenAsset, CitizenLod } from '../world/life/citizens/citizenAsset';

export type Clothing = keyof typeof clothing;
export { clothing };

export function visitorFigure(asset: CitizenAsset, character: 'male' | 'female') {
  const name = character === 'male' ? 'burgher-2' : 'townswoman-1';
  const figure = asset.figures.find((item) => item.name === name);
  if (!figure) throw new Error(`Missing citizen model: ${name}`);
  return figure;
}

export function visitorGeometry(
  asset: CitizenAsset,
  lod: CitizenLod,
  character: 'male' | 'female',
  choice: Clothing
) {
  const geometry = citizenGeometry(asset, lod);
  const colours = geometry.getAttribute('color');
  const original = new Color(character === 'male' ? '#8b4c2e' : '#b39340');
  const selected = new Color(clothing[choice].colour);
  /* The baker shades each triangle before quantisation. Matching the dye's channel ratios
     preserves those shades and leaves skin, linen, hair and leather untouched. */
  const squared = original.r ** 2 + original.g ** 2 + original.b ** 2;
  for (let i = 0; i < colours.count; i++) {
    const r = colours.getX(i),
      g = colours.getY(i),
      b = colours.getZ(i);
    const shade = (r * original.r + g * original.g + b * original.b) / squared;
    if (
      Math.hypot(r - original.r * shade, g - original.g * shade, b - original.b * shade) <
      4 / 65535
    )
      colours.setXYZ(i, selected.r * shade, selected.g * shade, selected.b * shade);
  }
  return geometry;
}

export function visitorArmGeometry(
  asset: CitizenAsset,
  lod: CitizenLod,
  character: 'male' | 'female',
  choice: Clothing
) {
  const bones = new Set(
    ['upper_arm.L', 'forearm.L', 'hand.L'].map((bone) => asset.bones.indexOf(bone))
  );
  const faces: number[] = [];
  for (let triangle = 0; triangle < lod.triangles; triangle++) {
    const face = lod.faces.subarray(triangle * 6, triangle * 6 + 6);
    if (
      [...face.subarray(0, 3)].every((v) => {
        const joint = lod.joints[v * 4];
        return joint !== undefined && bones.has(joint);
      })
    )
      faces.push(...face);
  }
  return visitorGeometry(
    asset,
    { ...lod, triangles: faces.length / 6, faces: Uint16Array.from(faces) },
    character,
    choice
  );
}
